import Vue from 'vue';
import {
  HistoryItem,
  Order,
  OrderComment,
  OrderFilters,
  OrderPriority,
  OrdersResponse,
  OrderTransportMode,
  Quote
} from '@/interfaces/order.interface';
import { Module } from 'vuex';
import { RootState } from '@/store/store';
import { orderActions } from '@/store/orders/order.store.actions';
import { OrderDocument } from '@/interfaces/document.interface';
import { VuetifyPaginationInterface } from '@/interfaces/vuetify-pagination.interface';
import { User, UserGroup } from "@/interfaces/user.interface";

export const mutations = {
  setOrders(state: any, response: OrdersResponse) {
    Vue.set(state, 'orders', response.orders);
    Vue.set(state, 'page', response.page);
    Vue.set(state, 'itemsPerPage', response.itemsPerPage);
    Vue.set(state, 'totalItems', response.totalItems);
    Vue.set(state, 'pagination', {
      ...state.pagination,
      ...{totalItems: response.totalItems}
    });
  },
  addOrder(state: any, order: Order) {
    Vue.set(state, 'orders', [...state.orders, order]);
    Vue.set(state, 'totalItems', state.totalItems + 1);
  },
  setPagination(state: any, {pagination}: { pagination: VuetifyPaginationInterface }) {
    Vue.set(state, 'pagination', pagination);
  },
  setOrdersFilter(state: any, filters: any) {
    Vue.set(state, 'orderFilters', {...state.orderFilters, ...filters});
  },
  setStatuses(state: any, {statuses}: { statuses: string[] }) {
    Vue.set(state, 'statuses', [...state.statuses, ...statuses]);
  },
  setTransportModes(state: any, {transportModes}: { transportModes: OrderTransportMode[] }) {
    Vue.set(state, 'transportModes', [...state.transportModes, ...transportModes]);
  },
  setPriorities(state: any, {priorities}: { priorities: string[] }) {
    Vue.set(state, 'priorities', [...state.priorities, ...priorities]);
  },
  create(state: any, {order}: { order: Order }) {
    Vue.set(state, 'orders', [...state.orders, order]);
  },
  update(state: any, {order}: { order: Order }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => o.uuid === order.uuid ? {...o, ...order} : o)
    );
  },
  delete(state: any, {order}: { order: Order }) {
    Vue.set(state, 'orders', state.orders.filter((o: Order) => o.uuid !== order.uuid));
  },
  setHistoryItems(state: any, {orderUuid, historyItems}: { orderUuid: string, historyItems: HistoryItem[] }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => o.uuid === orderUuid ? {...o, ...{history: historyItems}} : o)
    );
  },
  setQuotes(state: any, {orderUuid, quotes}: { orderUuid: string, quotes: Quote[] }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => o.uuid === orderUuid ? {...o, ...{quotes}} : o)
    );
  },
  createQuote(state: any, {quote}: { quote: Quote }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.quotes) return {...o, quotes: [quote]};
          return {
            ...o,
            quotes: [...o.quotes, quote]
          };
        })
    );
  },
  updateQuote(state: any, {quote}: { quote: Quote }) {
    // TODO: Find the bug: after adding a new order, confirming it, it gets confirmed for all orders with empty quotes
    if (!quote.uuid) return;
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.quotes) return o;
          return {
            ...o,
            quotes: o.quotes.map((q: Quote) => q.uuid === quote.uuid ? quote : q)
          };
        })
    );
  },
  deleteQuote(state: any, {quote}: { quote: Quote }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.quotes) return o;
          return {
            ...o,
            quotes: o.quotes.filter((q: Quote) => q.uuid !== quote.uuid)
          };
        })
    );
  },
  setComments(state: any, {orderUuid, comments}: { orderUuid: string, comments: OrderComment[] }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => o.uuid === orderUuid ? {...o, ...{comments}} : o)
    );
  },
  createComment(state: any, {comment}: { comment: OrderComment }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.comments) return {...o, comments: [comment]};
          return {
            ...o,
            comments: [...o.comments, comment]
          };
        })
    );
  },
  updateComment(state: any, {comment}: { comment: OrderComment }) {
    // TODO: Find the bug: after adding a new order, confirming it, it gets confirmed for all orders with empty comments
    if (!comment.uuid) return;
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.comments) return o;
          return {
            ...o,
            comments: o.comments.map((q: OrderComment) => q.uuid === comment.uuid ? comment : q)
          };
        })
    );
  },
  deleteComment(state: any, {comment}: { comment: OrderComment }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.comments) return o;
          return {
            ...o,
            comments: o.comments.filter((q: OrderComment) => q.uuid !== comment.uuid)
          };
        })
    );
  },
  setDocuments(state: any, {orderUuid, documents}: { orderUuid: string, documents: OrderDocument[] }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => o.uuid === orderUuid ? {...o, ...{documents}} : o)
    );
  },
  createDocument(state: any, {document}: { document: OrderDocument }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.documents) return {...o, documents: [document]};
          return {
            ...o,
            documents: [...o.documents, document]
          };
        })
    );
  },
  updateDocument(state: any, {document}: { document: OrderDocument }) {
    // TODO: Find the bug: after adding a new order, confirming it, it gets confirmed for all orders with empty documents
    if (!document.uuid) return;
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.documents) return o;
          return {
            ...o,
            documents: o.documents.map((d: OrderDocument) => d.uuid === document.uuid ? document : d)
          };
        })
    );
  },
  deleteDocument(state: any, {document}: { document: OrderDocument }) {
    Vue.set(
        state,
        'orders',
        state.orders.map((o: Order) => {
          if (!o.documents) return o;
          return {
            ...o,
            documents: o.documents.filter((d: OrderDocument) => d.uuid !== document.uuid)
          };
        })
    );
  }
};

export interface OrdersState {
  orders: Order[],
  orderFilters: OrderFilters,
  statuses: string[],
  transportModes: OrderTransportMode[],
  priorities: OrderPriority[],
  shipperGroups: UserGroup[],
  logistics: User,
  createdAtRange: string[];
  pagination: VuetifyPaginationInterface,
  totalItems: number,
  page: number,
  itemsPerPage: number
}

export const ordersState: OrdersState = {
  orders: [],
  totalItems: 0,
  page: 1,
  itemsPerPage: 50,
  orderFilters: {
    search: '',
    status: [],
    consigneeCountryName: '',
    shipperCountryName: '',
    priority: {},
    shipperGroup: {},
    logistics: {},
    createdAtRange: [],
  },
  statuses: [],
  shipperGroups: [],
  transportModes: [],
  priorities: [],
  logistics: {},
  createdAtRange: [],
  pagination: {
    page: 1,
    itemsPerPage: 50
  }
};

export const ordersStoreModule: Module<OrdersState, RootState> = {
  namespaced: true,
  state: ordersState,
  actions: orderActions,
  mutations
};
